import React, { useEffect, useState } from "react";
import { Paper } from "@mui/material";
import Grid from "@mui/material/Grid2";
import PropTypes from "prop-types";
import { useElectricData } from "./useElectricDataList";
import { useDurationHandle } from "../calendarDuration/useDurationHandle.js";
import { DURATION } from "../calendarDuration/durationOption";
import { Chart } from "../../chart/chart.jsx";
import { GRAPH_TYPE } from "../../../constants/trendGraph/trendGraphType";
import { NAVIGATION_BAR_HEIGHT } from "../../../constants/layout.js";
import { useDispatch } from "react-redux";
import { updateChartLineName } from "../../../model/slice/chartSettingsSlice.js";
import { VESSEL_TYPE } from "../../../constants/constants.js";

const styles = {
  paper: {
    backgroundColor: "inherit",
    boxShadow: "none",
    height: `calc(100dvh - ${NAVIGATION_BAR_HEIGHT}px - 230px)`,
  },
  container: {
    width: "100% !important",
  },
};

export const ElectricPanel = (props) => {
  const { 
    vesselId = null,
    currentVessel, 
    trendCharts, 
    setGraphData, 
    setDataFetchStatus, 
    isVesselOverview,
    setChartRef,
    selectedChart,
    setGraphConfig,
  } = props;
  const dispatch = useDispatch();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const isSEPVessel = currentVessel.vesselType === VESSEL_TYPE.SEP;
  const setValueOrDefault = (val) => (val > 0 ? val : 0);
  const [ge, setGe] = useState(setValueOrDefault(currentVessel?.NoDG));
  const [sg, setSg] = useState(setValueOrDefault(currentVessel?.NoSG));
  const { graphData, graphConfig, isLoading, isNoData, isTimeout } = useElectricData({
    vesselId: currentVessel?.id,
    ge,
    sg,
    selectedChart,
    isVesselOverview,
    isSEPVessel,
  });
  
  useEffect(() => {
    setDataFetchStatus({
      isLoading,
      isNoData,
    });
  }, [isLoading, isNoData]);

  useEffect(() => {
    setIsInitialLoad(true);
  }, [vesselId]);
  useEffect(() => {
    setGe(setValueOrDefault(currentVessel?.NoDG));
    setSg(setValueOrDefault(currentVessel?.NoSG));
  }, [currentVessel]);

  useEffect(() => {
    const isFetchingData = graphData?.length === 0 && !isNoData;
    const hasGenerators = ge > 0 || sg > 0;
    setIsInitialLoad((isFetchingData && hasGenerators) || isLoading);
    setGraphData(graphData);
    setGraphConfig(graphConfig);
    dispatch(updateChartLineName(graphConfig))
  }, [graphData, graphConfig, isNoData, isLoading, ge, sg]);

  // initial load trigger
  useDurationHandle(DURATION.ELECTRIC, null);

  return (
    <Grid spacing={2} container sx={styles.container}>
      <Grid item size={{xs:12}} >
        <Paper sx={styles.paper} id="trendGraph">
          <Chart
            isTimeout={isTimeout}
            isInitialLoad={isInitialLoad}
            isGraphDataLoading={isLoading}
            graphData={graphData}
            vesselId={vesselId}
            geNumber={ge}
            sgNumber={sg}
            isNoData={isNoData}
            type={GRAPH_TYPE.ELECTRIC}
            chartType={selectedChart}
            customLineData={trendCharts?.[GRAPH_TYPE.ELECTRIC]}
            isVesselOverview={isVesselOverview}
            setChartRef={setChartRef}
            vessel={currentVessel}
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

ElectricPanel.propTypes = {
  vesselId: PropTypes.string,
  currentVessel: PropTypes.object,
  trendCharts: PropTypes.array,
  setGraphData: PropTypes.func,
  isVesselOverview: PropTypes.bool,
  setChartRef: PropTypes.func,
  setDataFetchStatus: PropTypes.func,
  selectedChart: PropTypes.string,
  setGraphConfig: PropTypes.func,
};
